<template>
  <div
    class="tw-w-full"
    @keydown="onInputKeydown"
    @input="onInputTyped"
    @change.capture="setIsCommittingTypedTextOnBlur(true)"
    @change="setIsCommittingTypedTextOnBlur(false)"
  >
    <!-- The change listeners bracket the library's own handler on the input's
         native change event, so isCommittingTypedTextOnBlur is up only while
         the library commits typed text on blur. Kept inside the root: a comment
         beside it makes a second root node in dev builds, and $el stops being
         this div. -->
    <label v-if="textLabel" class="tw-block tw-mb-1 tw-text-xs tw-font-medium tw-uppercase tw-tracking-wide tw-text-slate-500">{{ textLabel }}<slot name="label-suffix" /></label>
    <DatePicker
      v-bind="$attrs"
      :value="resolvedPickerValue"
      :type="resolvedType"
      :format="resolvedFormat"
      :open="isPopupOpen"
      @update:open="setIsPopupOpen"
      @update:value="onUpdateValue"
      @change="onChange"
      @input-error="clearTypedPreviewValue"
      input-class="tw-w-full tw-rounded-xl tw-border tw-border-slate-200/70 tw-bg-white/60 tw-px-3 tw-py-2 tw-text-sm tw-text-slate-900 placeholder:tw-text-slate-400 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-indigo-500/20"
      :popup-class="resolvedPopupClass"
      :append-to-body="true"
      :popup-style="resolvedPopupStyle"
      :show-time-panel="getIsTimePanelBesideCalendar || undefined"
    >
      <!-- Only registered when the controls belong in the popup, otherwise the
           library would render an empty bordered footer strip. -->
      <template v-if="getIsShowingControlsInPopup" #footer>
        <DateTimePickerControls
          :allow-time-toggling="allowTimeToggling"
          :show-days-shortcuts="showDaysShortcuts"
          :is-time-enabled="isTimeEnabled"
          :is-disabled="getIsDisabled"
          class="tw-justify-start"
          @toggle-time="toggleIsTimeEnabled"
          @set-today="setDateToToday"
          @shift-days="shiftDateByDays"
        />
      </template>
    </DatePicker>

    <DateTimePickerControls
      v-if="getIsShowingControlsBelowInput"
      :allow-time-toggling="allowTimeToggling"
      :show-days-shortcuts="showDaysShortcuts"
      :is-time-enabled="isTimeEnabled"
      :is-disabled="getIsDisabled"
      class="tw-mt-1.5"
      @toggle-time="toggleIsTimeEnabled"
      @set-today="setDateToToday"
      @shift-days="shiftDateByDays"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import moment from 'moment';
import DatePicker from 'vue-datepicker-next';
import 'vue-datepicker-next/index.css';
import DateTimePickerControls from './DateTimePickerControls.vue';

// Two Shift presses this close together count as the "today" shortcut.
const DOUBLE_SHIFT_WINDOW_MS = 400;

export default defineComponent({
  name: 'BaseDateTimePicker',
  components: { DatePicker, DateTimePickerControls },
  inheritAttrs: false,
  props: {
    modelValue: {
      type: [Date, String, Number],
      default: null,
    },
    textLabel: {
      type: String,
      default: '',
    },
    // Takes keyboard focus on mount, without opening the menu. Set it on the
    // first field of a form or modal so the user can start tabbing from there.
    autofocus: {
      type: Boolean,
      default: false,
    },
    // Whether the picker edits a time of day alongside the date. Two-way:
    // bind it with v-model:has-time to persist what the time toggle chose.
    hasTime: {
      type: Boolean,
      default: false,
    },
    // Shows the clock button that switches between date and datetime.
    allowTimeToggling: {
      type: Boolean,
      default: false,
    },
    // Shows the [today] / minus one day / plus one day buttons.
    showDaysShortcuts: {
      type: Boolean,
      default: false,
    },
    dateFormat: {
      type: String,
      default: 'MM/DD/YYYY',
    },
    dateTimeFormat: {
      type: String,
      default: 'MM/DD/YYYY HH:mm',
    },
    // 'below-input' or 'popup': where the toggle and shortcuts are rendered.
    controlsPlacement: {
      type: String,
      default: 'below-input',
    },
    // 'over-calendar' or 'beside-calendar': where the datetime popup draws its
    // hour/minute strips. Beside the calendar they are narrow and always shown.
    timePanelPlacement: {
      type: String,
      default: 'over-calendar',
    },
    // Gap in px between the input and the popup, applied in whichever
    // direction the popup opens.
    popupOffset: {
      type: Number,
      default: 8,
    },
  },
  emits: ['update:modelValue', 'change', 'update:hasTime'],
  data() {
    return {
      // Seeded from the prop rather than read straight off it, so a caller that
      // does not bind v-model:has-time still gets a working toggle.
      isTimeEnabled: this.hasTime,
      // Binding the library's `open` prop routes every show/hide through this
      // component, which is what lets it ignore the unwanted ones.
      isPopupOpen: false,
      // Raised only while focus is being handed back to the input after a
      // change. See setIsPopupOpen for what it suppresses.
      isReturningFocusToInput: false,
      // Raised only across the close the library fires right after a date cell
      // is picked. See setIsPopupOpen for what it suppresses.
      isHoldingPopupOpenThroughClose: false,
      // Raised only while the library commits typed text because the input
      // lost focus. See onChange for what it changes.
      isCommittingTypedTextOnBlur: false,
      // The typed text as a value, while it parses as a complete date. Shown in
      // the calendar and time panel only; never emitted.
      typedPreviewValue: null as Date | number | string | null,
      // When Shift was last pressed with no other key after it. Zero once any
      // other key lands, so Shift+letter never counts toward a double Shift.
      lastLoneShiftPressedAt: 0,
    };
  },
  mounted() {
    if (this.autofocus) {
      const inputEl = this.$el?.querySelector('input') as HTMLInputElement | null;
      inputEl.focus()
    }
  },
  computed: {
    // The component owns type/format only while the time toggle is available;
    // otherwise a caller-supplied type/format attr still wins.
    getIsOwningDateTimeFormat(): boolean {
      return this.allowTimeToggling;
    },
    resolvedType(): string {
      if (this.getIsOwningDateTimeFormat) {
        return this.isTimeEnabled ? 'datetime' : 'date';
      }
      const attrType = (this.$attrs as any).type;
      return attrType || (this.isTimeEnabled ? 'datetime' : 'date');
    },
    resolvedFormat(): string {
      if (this.getIsOwningDateTimeFormat) {
        return this.isTimeEnabled ? this.dateTimeFormat : this.dateFormat;
      }
      const attrFormat = (this.$attrs as any).format;
      return attrFormat || (this.isTimeEnabled ? this.dateTimeFormat : this.dateFormat);
    },
    // Mirrors vue-datepicker-next's own value-type prop, so the shortcut
    // buttons emit the same shape the picker itself would emit.
    getValueType(): string {
      const attrs = this.$attrs as any;
      return attrs['value-type'] || attrs.valueType || 'date';
    },
    // The library draws its calendar and time panel from its value prop, so
    // handing it the typed preview is what makes them follow the typing.
    resolvedPickerValue(): Date | number | string | null {
      return this.typedPreviewValue ?? this.modelValue;
    },
    getIsDisabled(): boolean {
      const disabled = (this.$attrs as any).disabled;
      return disabled === true || disabled === '';
    },
    getHasAnyControls(): boolean {
      return this.allowTimeToggling || this.showDaysShortcuts;
    },
    // The popup footer only makes sense over a calendar, not over the time,
    // week, month or year panels.
    getIsCalendarPopup(): boolean {
      return this.resolvedType === 'date' || this.resolvedType === 'datetime';
    },
    getIsShowingControlsInPopup(): boolean {
      return this.getHasAnyControls && this.controlsPlacement === 'popup' && this.getIsCalendarPopup;
    },
    getIsShowingControlsBelowInput(): boolean {
      return this.getHasAnyControls && !this.getIsShowingControlsInPopup;
    },
    getIsTimePanelBesideCalendar(): boolean {
      return this.timePanelPlacement === 'beside-calendar' && this.resolvedType === 'datetime';
    },
    // The modifier class scopes the beside-calendar overrides in the style
    // block below to this picker's popup only.
    resolvedPopupClass(): string {
      const popupClass = 'tw-rounded-2xl tw-border tw-border-slate-200/70 tw-bg-white tw-shadow-xl tw-shadow-slate-900/10';
      return this.getIsTimePanelBesideCalendar ? `${popupClass} base-date-time-picker-popup--time-beside` : popupClass;
    },
    // The library measures the popup's vertical margins as part of its height,
    // so equal margins offset the popup whether it opens downward or upward.
    resolvedPopupStyle(): Record<string, string | number> {
      return {
        zIndex: 4000,
        marginTop: `${this.popupOffset}px`,
        marginBottom: `${this.popupOffset}px`,
      };
    },
  },
  watch: {
    hasTime(newHasTime: boolean) {
      this.isTimeEnabled = newHasTime;
    },
  },
  methods: {
    // Turns the bound value into a moment, whatever value-type produced it.
    parseModelValueToMoment(): moment.Moment | null {
      const value = this.modelValue;
      if (value === null || value === undefined || value === '') return null;

      const valueType = this.getValueType;
      let parsed: moment.Moment;
      if (valueType === 'date') {
        parsed = moment(value as Date);
      } else if (valueType === 'timestamp') {
        parsed = moment(Number(value));
      } else if (valueType === 'format') {
        parsed = moment(value as string, this.resolvedFormat);
      } else {
        parsed = moment(value as string, valueType);
      }
      return parsed.isValid() ? parsed : null;
    },
    // Converts a moment back into the shape value-type asks for.
    buildModelValueFromMoment(source: moment.Moment): Date | number | string {
      const valueType = this.getValueType;
      if (valueType === 'date') return source.toDate();
      if (valueType === 'timestamp') return source.valueOf();
      if (valueType === 'format') return source.format(this.resolvedFormat);
      return source.format(valueType);
    },
    emitModelValue(source: moment.Moment) {
      const value = this.buildModelValueFromMoment(source);
      this.$emit('update:modelValue', value);
      this.onChange(value, 'date');
    },
    // Existing value's time of day, or a sensible default when there is none.
    resolveBaseMoment(): moment.Moment {
      const current = this.parseModelValueToMoment();
      if (current) return current;
      return this.isTimeEnabled ? moment() : moment().startOf('day');
    },
    toggleIsTimeEnabled() {
      if (this.getIsDisabled) return;

      // Read the value while the old format still applies, then switch.
      const current = this.parseModelValueToMoment();
      this.isTimeEnabled = !this.isTimeEnabled;
      // Callers persist this: the stored value is a datetime either way, so the
      // flag is the only record of whether the time of day is meaningful.
      this.$emit('update:hasTime', this.isTimeEnabled);
      if (!current) return;

      // Re-emit so the bound value matches what the input now shows: dropping
      // the time truncates to midnight, and value-type="format" values are
      // re-stringified in the new format.
      this.emitModelValue(this.isTimeEnabled ? current : current.startOf('day'));
    },
    // The next full hour from now (14:37 or 14:00 both give 15:00), held at
    // 23:00 late in the evening so the date never rolls over to tomorrow.
    buildNextFullHourToday(): moment.Moment {
      const now = moment();
      const nextFullHour = now.clone().startOf('hour').add(1, 'hour');
      if (!nextFullHour.isSame(now, 'day')) return now.clone().hour(23).startOf('hour');
      return nextFullHour;
    },
    // Keeps an existing value's time of day and only moves its date to today.
    // An empty field gets the next full hour, or midnight when time is off.
    setDateToToday() {
      if (this.getIsDisabled) return;
      const current = this.parseModelValueToMoment();
      if (!current) {
        this.emitModelValue(this.isTimeEnabled ? this.buildNextFullHourToday() : moment().startOf('day'));
        return;
      }
      const today = moment();
      this.emitModelValue(current.clone().year(today.year()).month(today.month()).date(today.date()));
    },
    shiftDateByDays(dayCount: number) {
      if (this.getIsDisabled) return;
      this.emitModelValue(this.resolveBaseMoment().clone().add(dayCount, 'days'));
    },
    // True while the input shows text the library has not committed yet. The
    // library keeps showing that text over the bound value until Enter or blur,
    // and commits it on blur, so a shortcut fired now would be overwritten.
    getHasUncommittedInputText(inputEl: HTMLInputElement): boolean {
      const committedText = this.parseModelValueToMoment()?.format(this.resolvedFormat) ?? '';
      return inputEl.value !== committedText;
    },
    // Keyboard versions of the days shortcut buttons, typed into the input:
    // double Shift for today, Arrow Up / Arrow Down for plus / minus one day.
    onInputKeydown(evt: KeyboardEvent) {
      if (!this.showDaysShortcuts || this.getIsDisabled) return;
      const inputEl = evt.target as HTMLElement;
      if (!(inputEl instanceof HTMLInputElement)) return;

      if (evt.key !== 'Shift') {
        this.lastLoneShiftPressedAt = 0;
      }

      if (evt.key === 'Shift') {
        if (evt.repeat) return;
        const now = Date.now();
        const isDoubleShift = now - this.lastLoneShiftPressedAt <= DOUBLE_SHIFT_WINDOW_MS;
        this.lastLoneShiftPressedAt = isDoubleShift ? 0 : now;
        if (isDoubleShift && !this.getHasUncommittedInputText(inputEl)) {
          this.setDateToToday();
        }
      } else if (evt.key === 'ArrowUp' || evt.key === 'ArrowDown') {
        // Stops the caret jumping to the start or end of the text.
        evt.preventDefault();
        if (this.getHasUncommittedInputText(inputEl)) return;
        this.shiftDateByDays(evt.key === 'ArrowUp' ? 1 : -1);
      }
    },
    // The library keeps typed text to itself until Enter or blur, so its popup
    // would show the old value meanwhile. Strict parsing in the display format
    // matches what the library will accept when it commits.
    onInputTyped(evt: Event) {
      const inputEl = evt.target;
      if (!(inputEl instanceof HTMLInputElement)) return;
      const parsed = moment(inputEl.value.trim(), this.resolvedFormat, true);
      this.typedPreviewValue = parsed.isValid() ? this.buildModelValueFromMoment(parsed) : null;
    },
    clearTypedPreviewValue() {
      this.typedPreviewValue = null;
    },
    setIsCommittingTypedTextOnBlur(isCommitting: boolean) {
      this.isCommittingTypedTextOnBlur = isCommitting;
    },
    onUpdateValue(val: any) {
      this.clearTypedPreviewValue();
      this.$emit('update:modelValue', val);
    },
    // Mirrors whatever visibility the library asks for, minus two requests it
    // makes that this component does not want: the open that rides on the focus
    // event it fires at the input, and the close that follows a date cell pick.
    setIsPopupOpen(isOpen: boolean) {
      if (isOpen && this.isReturningFocusToInput) return;
      if (!isOpen && this.isHoldingPopupOpenThroughClose) return;
      this.isPopupOpen = isOpen;
    },
    // Puts the caret back in the text input, leaving the popup however the
    // change left it: still open over a datetime date pick, closed otherwise.
    focusInputWithoutReopeningPopup() {
      const inputEl = this.$el?.querySelector('input') as HTMLInputElement | null;
      if (!inputEl) return;

      // focus() dispatches its focus event synchronously, so the flag only has
      // to cover this one call.
      this.isReturningFocusToInput = true;
      inputEl.focus({ preventScroll: true });
      this.isReturningFocusToInput = false;
    },
    // changeType is how the value was committed: 'date' for a calendar cell,
    // 'time' for the time panel, undefined for text typed into the input.
    onChange(val: any, changeType?: string) {
      this.$emit('change', val);
      // The library closes the popup as soon as this returns. Two cases hold it
      // open: picking a day, so the calendar stays up for a time or a second
      // date; and typed text committed on blur, because that blur is the mouse
      // pressing inside the popup and the click still has to land there.
      // Enter and a time pick still close it. Tab and clicking outside have
      // already closed it by the time a blur commit runs.
      this.isHoldingPopupOpenThroughClose = changeType === 'date' || this.isCommittingTypedTextOnBlur;
      // Focus left on purpose for a blur commit, so it is not pulled back.
      const isReturningFocus = !this.isCommittingTypedTextOnBlur;

      // The close lands after onChange returns, so wait a tick to act on the
      // visibility the library actually settled on.
      this.$nextTick(() => {
        this.isHoldingPopupOpenThroughClose = false;
        if (isReturningFocus) this.focusInputWithoutReopeningPopup();
      });
    }
  }
});
</script>

<!-- Unscoped: the popup is appended to <body> and its insides are rendered by
     the library, so scoped styles cannot reach them. The modifier class keeps
     these rules to popups that asked for the beside-calendar layout. -->
<style lang="scss">
.base-date-time-picker-popup--time-beside {
  // The library stacks the time panel absolutely over a fixed-size calendar
  // box; lay the two out side by side instead. The box keeps its fixed height,
  // which caps the time strips to the calendar's height.
  .mx-date-time {
    display: flex;
    width: auto;
  }

  .mx-date-time .mx-time {
    position: static;
    flex: none;
    width: auto;
    height: 100%;
    border-left: 1px solid #e2e8f0; // tailwind slate-200
  }

  // Its title only switches back to the calendar, which is already visible.
  .mx-time-header {
    display: none;
  }

  .mx-time-content {
    height: 100%;
  }

  .mx-time-column {
    flex: none;
    width: 60px;
  }
}
</style>
